import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { LivechatSubjectType, LiveChatUser } from "@scrile/api-provider/dist/api/LivechatsProvider";
import { Subject } from "@scrile/api-provider/dist/api/SubjectProvider";
import { parseISO } from "@scrile/tools/dist/lib/TimeHelpers";
import useAuthUser from "hooks/useAuthUser";
import useConnectionReconnect from "hooks/useConnectionReconnect";
import useConsumeController from "hooks/useConsumeController";
import useLiveChat from "hooks/useLiveChat";
import useLiveChatName from "hooks/useLiveChatName";
import useNetworkQuality from "hooks/useNetworkQuality";
import useProduceController from "hooks/useProduceController";
import useRemoteNetworkQualities from "hooks/useRemoteNetworkQualities";
import useStreamHelper from "hooks/useStreamHelper";
import useWebRTC from "hooks/useWebRTC";
import { setRecordingUploadPaused } from "lib/recordingSegmentUploader";
import { ProduceUser } from "types";

export default function useProduceConsumeController(livechatSubject: Subject<LivechatSubjectType>, ownerId: string) {
  const { user } = useAuthUser();
  const location = useLocation();
  const voiceOnly = useMemo(() => new URLSearchParams(location.search).get("voiceOnly") === "1", [location.search]);
  const webRTC = useWebRTC();
  const { liveChatName } = useLiveChatName(user?.screenName ?? "");
  const { loading, token, joinData, endChat, clientServerTimeDiff, toggleMuteUser: muteUser } = useLiveChat({
    livechatSubject,
    liveChatName,
  });

  const {
    stream: produceStream,
    screenStream: produceScreenStream,
    streamToProduce: produceFullStream,
    mediaConstraints: produceMediaConstraints,
    closeStream: closeProduceStream,
    onChangeConstraints,
    onProduceStream,
    audioInputs,
    videoInputs,
    processing,
    loading: loadingProduceData,
    setBackgroundImage,
    setBackgroundMode,
    producerSpeaking,
  } = useProduceController({ token, joinData, autoProduceStream: true, voiceOnly });

  const { closeStream: closeConsumeStream, users } = useConsumeController({
    joinData,
    token,
  });

  const { checkStreamForChanges } = useStreamHelper();

  const streamStartedDate = useMemo(() => {
    if (!joinData) {
      return "";
    }
    return new Date(parseISO(joinData.me.joinTime).getTime() + (clientServerTimeDiff ?? 0)).toISOString();
  }, [clientServerTimeDiff, joinData]);

  const localProduceStream = useRef<MediaStream>(produceStream);
  const localProduceScreenStream = useRef<MediaStream>(produceScreenStream);
  // RC-5: derive both directly (not via setState-in-effect) so React commits
  // the roster (signaling-driven) and the viewable grid (WebRTC-consume-driven)
  // in the SAME render, minimizing the visible skew between them.
  const userList = useMemo(() => joinData?.users.filter((user) => user.broadcasting) ?? [], [joinData]);
  const usersToView = useMemo(() => Object.values(users), [users]);
  const [me, setMe] = useState<ProduceUser | null>(null);

  useEffect(() => {
    if (!produceStream || !localProduceStream.current) return;
    checkStreamForChanges(produceStream, localProduceStream.current);
  }, [produceStream, checkStreamForChanges]);

  useEffect(() => {
    if (!produceScreenStream || !localProduceScreenStream.current) return;
    checkStreamForChanges(produceScreenStream, localProduceScreenStream.current);
  }, [produceScreenStream, checkStreamForChanges]);

  useEffect(() => {
    setMe(
      joinData
        ? {
            user: joinData.me,
            stream: produceStream,
            screenStream: produceScreenStream,
            audioStream: produceFullStream,
            constrains: produceMediaConstraints,
            isSpeaking: producerSpeaking,
          }
        : null
    );
  }, [joinData, produceMediaConstraints, produceStream, produceScreenStream, produceFullStream, producerSpeaking]);

  const prevMuted = useRef(me?.user?.muted);
  useEffect(() => {
    if (me?.user?.muted && !prevMuted.current) {
      onChangeConstraints({ audioEnabled: false });
    }
    prevMuted.current = me?.user?.muted;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.user?.muted]);

  useEffect(() => {
    window.addEventListener("beforeunload", endChat);
    return () => {
      window.removeEventListener("beforeunload", endChat);
    };
  }, [endChat]);

  const isConferenceOwner = useMemo(() => me?.user.userId === ownerId, [me, ownerId]);

  const toggleMuteUser = async (user: LiveChatUser) => {
    if (!isConferenceOwner) return;
    await muteUser(user);
  };

  const onCloseStream = async () => {
    try {
      await closeProduceStream();
    } catch {
      /* ignore */
    }
    try {
      closeConsumeStream();
    } catch {
      /* ignore */
    }
    try {
      await endChat();
    } catch {
      /* ignore */
    }
  };

  const handleReconnect = useCallback(async () => {
    // eslint-disable-next-line no-console
    console.info("[reconnect] Re-producing stream...");
    await onProduceStream();
  }, [onProduceStream]);

  // Full rejoin path: invoked after MAX_RETRIES of `onReconnect` failed.
  // Tears down the livechat broadcast (re-broadcast on top of the same
  // token is enough — `onProduceStream` already calls `broadcast` and
  // rebuilds the producer end-to-end). If the livechat session itself
  // is dead (e.g. keepAlive bailed out, server lost the join), the
  // recovery path is a page reload — surfaced as a banner instead of
  // hidden as a silent no-op.
  const handleRejoin = useCallback(async () => {
    // eslint-disable-next-line no-console
    console.info("[reconnect] Auto-rejoin: re-broadcasting livechat session");
    await onProduceStream();
  }, [onProduceStream]);

  const { connectionStatus, rejoinInSeconds, rejoinNow } = useConnectionReconnect({
    webRTC,
    onReconnect: handleReconnect,
    onRejoin: handleRejoin,
    enabled: !!joinData,
  });

  const { quality: networkQuality } = useNetworkQuality(webRTC, !!joinData);
  const remoteQualities = useRemoteNetworkQualities(users, !!joinData);

  // Recording bandwidth back-pressure: when our outgoing link is bad
  // (quality ≤ 2 of 5) the parallel 1-second segment uploads compound
  // the producer's RTP loss and tip the call into ICE failure. Pause
  // the uploader; it keeps buffering chunks in memory so nothing is
  // lost. Resume at quality ≥ 4 — leaves a hysteresis gap so we don't
  // toggle on every borderline reading.
  const lastQualityRef = useRef<number | null>(null);
  useEffect(() => {
    if (!joinData) return;
    if (lastQualityRef.current !== networkQuality) {
      // Promote to warn so diagnosticDump captures every quality
      // transition. Sustained low quality (e.g. 100+ s at ≤2) is the
      // canonical signal that SFU/network is degraded — needed to
      // post-mortem 2-3 min drops.
      // eslint-disable-next-line no-console
      console.warn(`[NET-QUALITY] conference quality=${lastQualityRef.current}→${networkQuality}`);
      lastQualityRef.current = networkQuality;
    }
    if (networkQuality <= 2) {
      setRecordingUploadPaused(true);
    } else if (networkQuality >= 4) {
      setRecordingUploadPaused(false);
    }
    // The intermediate value (3) keeps whatever state we're in; that
    // intentional gap is the hysteresis.
  }, [networkQuality, joinData]);
  // Make sure we don't leave the recorder paused once we leave the conf.
  useEffect(() => {
    return () => { setRecordingUploadPaused(false); };
  }, []);

  return {
    threadId: joinData?.threadId,
    userList,
    produceMediaConstraints,
    loading: loading || loadingProduceData || processing,
    token,
    audioInputs,
    videoInputs,
    users: usersToView,
    me,
    isConferenceOwner,
    streamStartedDate,
    connectionStatus,
    rejoinInSeconds,
    rejoinNow,
    networkQuality,
    remoteQualities,
    onChangeConstraints,
    onCloseStream,
    toggleMuteUser,
    setBackgroundImage,
    setBackgroundMode,
    webRTC,
  };
}
